import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { Printer, FileSignature, ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Badge } from "@/components/ui/Badge";
import { DatePicker } from "@/components/ui/DatePicker";
import { PageLoader } from "@/components/ui/Spinner";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { DesignDocumentStatus, ClusterSigningMode, ManagedFileBrief } from "../types";
import { STATUS_LABELS } from "../types";

interface RevisionLogEntry {
  id: string;
  revision_number: number;
  status: DesignDocumentStatus;
  reason: string;
  document_title: string;
  cluster_id: string;
  cluster_name: string;
  cluster_signing_mode: ClusterSigningMode;
  project_id: string;
  project_name: string;
  contractor_name: string;
  created_by_name: string | null;
  created_at: string;
  signed_at: string | null;
  acknowledged_at: string | null;
  original_file: ManagedFileBrief | null;
  signed_file: ManagedFileBrief | null;
}

interface RevisionLogFilters {
  status?: string;
  signing_mode?: string;
  date_from?: string;
  date_to?: string;
}

type SortKey = "document_title" | "cluster_name" | "created_by_name" | "status" | "created_at" | "signed_at";

function useRevisionLog(filters: RevisionLogFilters) {
  const params: Record<string, string> = {};
  if (filters.status) params.status = filters.status;
  if (filters.signing_mode) params.signing_mode = filters.signing_mode;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;

  return useQuery({
    queryKey: ["doc-revision-log", filters],
    queryFn: () =>
      apiClient
        .get<{
          data: { results: RevisionLogEntry[] };
          meta: { count: number };
        }>("/project-docs/revision-log/", { params })
        .then((r) => r.data),
    staleTime: 30_000,
  });
}

const STATUS_BADGE_COLOR: Record<string, "yellow" | "green" | "red" | "gray"> = {
  pending_signature: "yellow",
  signed: "green",
  rejected: "red",
};

function fmt(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SignerMultiSelect({ options, selected, onChange }: {
  options: string[];
  selected: Set<string>;
  onChange: (s: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const label = selected.size === 0
    ? "Wszystkie"
    : selected.size === 1
      ? ([...selected][0].length > 22 ? [...selected][0].slice(0, 20) + "…" : [...selected][0])
      : `${selected.size} firm`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-sm border border-gray-200 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 flex items-center gap-1.5 min-w-36"
      >
        <span className="flex-1 truncate text-left">{label}</span>
        <ChevronDown className={clsx("h-3 w-3 shrink-0 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute z-20 top-full mt-1 left-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg min-w-52 max-h-64 overflow-y-auto">
          {options.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-400">Brak danych</p>
          ) : (
            options.map((name) => (
              <label key={name} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.has(name)}
                  onChange={() => {
                    const next = new Set(selected);
                    if (next.has(name)) next.delete(name); else next.add(name);
                    onChange(next);
                  }}
                  className="h-3.5 w-3.5 accent-blue-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{name}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SortIcon({ col, sortKey, sortDir }: { col: SortKey; sortKey: SortKey; sortDir: "asc" | "desc" }) {
  if (sortKey !== col) return <ChevronsUpDown className="h-3 w-3 text-gray-300 dark:text-gray-600" />;
  return sortDir === "asc"
    ? <ChevronUp className="h-3 w-3 text-gray-500 dark:text-gray-400" />
    : <ChevronDown className="h-3 w-3 text-gray-500 dark:text-gray-400" />;
}

export function DocActivityPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const isContractor = user?.role === "contractor";
  const isDirector = user?.role === "director";

  const [statusFilter, setStatusFilter] = useState("");
  const [signingModeFilter, setSigningModeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [signerFilter, setSignerFilter] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const { data, isLoading } = useRevisionLog({
    status: statusFilter || undefined,
    signing_mode: signingModeFilter || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  const entries = data?.data.results ?? [];
  const total = data?.meta.count ?? 0;

  const uniqueContractors = [...new Set(
    entries.map((e) => e.contractor_name).filter(Boolean) as string[]
  )].sort((a, b) => a.localeCompare(b, "pl"));

  const filteredEntries = entries.filter((e) =>
    signerFilter.size === 0 || signerFilter.has(e.contractor_name ?? "")
  );

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    const av = a[sortKey] ?? "";
    const bv = b[sortKey] ?? "";
    const cmp = String(av).localeCompare(String(bv), "pl");
    return sortDir === "asc" ? cmp : -cmp;
  });

  function handleSort(key: SortKey) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  }

  function ThSort({ col, children }: { col: SortKey; children: React.ReactNode }) {
    return (
      <th
        className="px-4 py-2 text-left font-medium cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
        onClick={() => handleSort(col)}
      >
        <div className="flex items-center gap-1">
          {children}
          <SortIcon col={col} sortKey={sortKey} sortDir={sortDir} />
        </div>
      </th>
    );
  }

  function clearFilters() {
    setStatusFilter(""); setSigningModeFilter(""); setDateFrom(""); setDateTo("");
    setSignerFilter(new Set());
  }

  const hasActiveFilters = !!(statusFilter || signingModeFilter || dateFrom || dateTo || signerFilter.size > 0);

  function handleRowClick(entry: RevisionLogEntry) {
    if (isContractor) {
      navigate(`/contractor/documentation/${entry.cluster_id}`);
    } else {
      navigate(`/projects/${entry.project_id}/documentation/${entry.cluster_id}`);
    }
  }

  if (isLoading) return <PageLoader />;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Weryfikacja dokumentów
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Historia rewizji dokumentacji projektowej
            {total > 0 && (
              <span className="ml-2 text-xs text-gray-400">({total} rekordów)</span>
            )}
          </p>
        </div>
      </div>

      {isDirector && (
        <div className="flex flex-wrap gap-3 mb-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Wszystkie</option>
              <option value="pending_signature">Oczekuje na sprawdzenie</option>
              <option value="signed">Sprawdzone</option>
              <option value="rejected">Odrzucone</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Tryb</label>
            <select
              value={signingModeFilter}
              onChange={(e) => setSigningModeFilter(e.target.value)}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Wszystkie</option>
              <option value="electronic">Elektroniczne</option>
              <option value="paper">Papierowe</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Podwykonawca</label>
            <SignerMultiSelect
              options={uniqueContractors}
              selected={signerFilter}
              onChange={setSignerFilter}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Od</label>
            <DatePicker
              value={dateFrom}
              onChange={setDateFrom}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Do</label>
            <DatePicker
              value={dateTo}
              onChange={setDateTo}
            />
          </div>
          {hasActiveFilters && (
            <div className="flex items-end">
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 py-1 px-2"
              >
                Wyczyść filtry
              </button>
            </div>
          )}
        </div>
      )}

      {sortedEntries.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center py-12 text-gray-400 dark:text-gray-500">
          <p>Brak rewizji dokumentów do wyświetlenia.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
              <tr>
                <ThSort col="document_title">Dokument</ThSort>
                <ThSort col="cluster_name">Klaster</ThSort>
                {isDirector && <ThSort col="created_by_name">Przekazał</ThSort>}
                <ThSort col="status">Status</ThSort>
                <ThSort col="created_at">Dodano</ThSort>
                <ThSort col="signed_at">Sprawdzono</ThSort>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortedEntries.map((entry) => {
                const isPaper = entry.cluster_signing_mode === "paper";
                return (
                  <tr
                    key={entry.id}
                    className={clsx(
                      "cursor-pointer transition-colors",
                      isPaper
                        ? "bg-amber-50/40 dark:bg-amber-950/10 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                        : "bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800"
                    )}
                    onClick={() => handleRowClick(entry)}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                        {entry.document_title}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        Wersja {entry.revision_number}
                        {entry.reason ? ` · ${entry.reason}` : ""}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {isPaper ? (
                          <Printer className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                        ) : (
                          <FileSignature className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                        )}
                        <div>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {entry.cluster_name}
                          </p>
                          {isDirector && (
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              {entry.project_name} · {entry.contractor_name}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    {isDirector && (
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {entry.created_by_name ?? "—"}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <Badge color={STATUS_BADGE_COLOR[entry.status] ?? "gray"}>
                        {STATUS_LABELS[entry.status]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {fmt(entry.created_at)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {fmt(entry.signed_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
